// Usage-based AWS cost estimation for chadha.surf.
//
// We don't pull live billing (AWS Cost Explorer is account-level, costs per
// request, and can't attribute spend per-trip). Instead we estimate from the
// usage we already track in Firestore (bytes stored per trip, movie durations)
// plus the known, always-on infra (Fargate task, ALB) and current on-demand
// us-east-1 prices. Every number here is an *estimate* with editable
// assumptions — good enough to see what drives the bill and per-trip cost.

export const HOURS_PER_MONTH = 730;
export const DAYS_PER_MONTH = 30.4;

// On-demand us-east-1 list prices (USD), approximate as of 2026.
export const PRICES = {
  // S3 storage ($/GB-month) by class.
  s3StandardGbMonth: 0.023,
  s3IaGbMonth: 0.0125, // Standard-IA
  s3GlacierIrGbMonth: 0.004, // Glacier Instant Retrieval (still millisecond access)
  s3GlacierGbMonth: 0.0036, // Glacier Flexible Retrieval
  s3DeepArchiveGbMonth: 0.00099, // Glacier Deep Archive
  // S3 requests.
  s3PutPer1k: 0.005,
  s3GetPer1k: 0.0004,
  // CloudFront.
  cloudfrontEgressGb: 0.085, // beyond the 1 TB/mo always-free tier
  cloudfrontFreeGb: 1024, // CloudFront perpetual free tier (egress)
  cloudfrontReqPer10k: 0.0075, // HTTPS requests, beyond 10M/mo always-free
  cloudfrontFreeReq: 10_000_000,
  // Fargate.
  fargateVcpuHr: 0.04048,
  fargateGbHr: 0.004445,
  fargateEphemeralGbHr: 0.000111, // for storage beyond the free 20 GB
  // ALB.
  albHr: 0.0225,
  albLcuHr: 0.008,
  // Misc fixed.
  ecrGbMonth: 0.1,
  secretsManagerSecretMonth: 0.4,
};

// The deployed backend task (see backend/task-def.json) — always-on, count 1.
export const INFRA = {
  fargateVcpu: 8,
  fargateGb: 16,
  fargateEphemeralGb: 50,
  albLcu: 1, // light, near-idle
  ecrGb: 2, // backend image(s)
  secrets: 1, // firebase-service-account
};

// Concrete resources behind the estimates above. Sourced from the live
// deployment (backend/task-def.json, provisioning) so the dollar figures map to
// real infrastructure. `driver` ties each row to the cost line it feeds.
export const INFRA_SPECS = [
  {
    group: 'Region',
    items: [{ name: 'AWS region', spec: 'us-east-1 (N. Virginia)', driver: 'All on-demand list prices' }],
  },
  {
    group: 'Compute',
    items: [
      {
        name: 'ECS Fargate task',
        spec: `${INFRA.fargateVcpu} vCPU · ${INFRA.fargateGb} GB RAM · ${INFRA.fargateEphemeralGb} GB ephemeral · always-on ×1`,
        driver: 'Backend compute (≈$' + Math.round((INFRA.fargateVcpu * PRICES.fargateVcpuHr + INFRA.fargateGb * PRICES.fargateGbHr) * HOURS_PER_MONTH) + '/mo)',
      },
      {
        name: 'Runtime',
        spec: 'Node 22 · ffmpeg · python3 · yt-dlp · onnxruntime · libraw · imagemagick · RED R3D SDK (C++)',
        driver: 'Render / waveform / reframe / export / RAW-develop / RED R3D proxy workers',
      },
    ],
  },
  {
    group: 'Networking',
    items: [
      { name: 'Application Load Balancer', spec: `1 ALB · ~${INFRA.albLcu} LCU (near-idle)`, driver: 'Load balancer line' },
      {
        name: 'CloudFront',
        spec: 'cdn.chadha.surf (movies) + upload API + Firebase frontend · HTTP/3 · OAC',
        driver: 'Streaming egress + requests',
      },
    ],
  },
  {
    group: 'Storage',
    items: [
      { name: 'S3 — chadha-surf-raw', spec: 'Raw footage & photos · full resolution (incl. RED .RDC folders) · Standard', driver: 'Raw footage storage' },
      { name: 'S3 — chadha-surf-movies', spec: 'HLS ladders, masters, social exports, RAW develop proxies, RED R3D HLS proxies · Standard', driver: 'Movie + derived storage + streaming' },
      { name: 'ECR', spec: `Backend container image (~${INFRA.ecrGb} GB)`, driver: 'Other (registry)' },
    ],
  },
  {
    group: 'Secrets & identity',
    items: [
      { name: 'Secrets Manager', spec: `${INFRA.secrets} secret (Firebase service account)`, driver: 'Other (secrets)' },
      { name: 'SSM Parameter Store', spec: 'YouTube API key + misc params', driver: 'Free tier' },
      { name: 'Firebase', spec: 'Hosting · Firestore · Auth (Email/Password)', driver: 'Spark free tier (assumed)' },
    ],
  },
];

// Default, user-editable assumptions (persisted in localStorage by the page).
export const DEFAULT_ASSUMPTIONS = {
  viewsPerMonth: 30, // full movie views / month (drives CloudFront egress)
  avgViewMbps: 5, // effective ABR bitrate during a view (~1080p + audio)
  backendAlwaysOn: true, // Fargate task running 24/7
  // Derived assets kept on the movies bucket (RAW-develop 16-bit proxies,
  // display thumbnails, graded exports, and RED .RDC → UHD HLS stream proxies),
  // as a fraction of raw bytes. RED proxies are tiny vs. the multi-GB originals
  // (~24 Mbps × clip length), so this envelope comfortably covers them.
  rawDeriveOverhead: 0.05,
};

// HLS ladder bitrate sum (kbps): 1080@5000 + 720@3000 + 480@1200 + 3×128 audio.
const LADDER_KBPS = 5000 + 3000 + 1200 + 128 * 3;
// Flat 1080p master MP4 (kbps) kept for reframing/social export.
const MASTER_KBPS = 8000 + 160;
// Avg HLS segment length (s) — used to estimate request counts per view.
const SEGMENT_SEC = 6;

export const GB = (bytes) => (bytes || 0) / 1e9;

// Money formatter that never hides a real cost as "$0.00": tiny-but-nonzero
// amounts show as "<$0.01", and non-finite values are treated as 0.
export const fmtUSD = (n) => {
  const v = Number.isFinite(n) ? n : 0;
  if (v > 0 && v < 0.01) return '<$0.01';
  if (v < 0 && v > -0.01) return '>-$0.01';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Storage classes the raw bucket can use, for the lifetime comparison.
export const STORAGE_CLASSES = [
  { id: 'STANDARD', label: 'S3 Standard', price: PRICES.s3StandardGbMonth, note: 'Instant access, no retrieval fee. Default.' },
  { id: 'STANDARD_IA', label: 'Standard-IA', price: PRICES.s3IaGbMonth, note: 'Instant access; per-GB retrieval fee + 30-day min.' },
  { id: 'GLACIER_IR', label: 'Glacier Instant Retrieval', price: PRICES.s3GlacierIrGbMonth, note: 'Millisecond access; higher retrieval fee + 90-day min. Best for lifetime archives you still want instantly.' },
  { id: 'GLACIER', label: 'Glacier Flexible', price: PRICES.s3GlacierGbMonth, note: 'Minutes–hours to restore; 90-day min.' },
  { id: 'DEEP_ARCHIVE', label: 'Glacier Deep Archive', price: PRICES.s3DeepArchiveGbMonth, note: 'Cheapest; 12+ h to restore; 180-day min.' },
];

export function storagePriceForClass(cls) {
  const found = STORAGE_CLASSES.find((c) => c.id === cls);
  return found ? found.price : PRICES.s3StandardGbMonth;
}

// Per-trip raw storage cost (originals kept full-res, instant access).
export function tripMonthlyStorage(trip) {
  return GB(trip.totalBytes) * storagePriceForClass(trip.storageClass);
}

// Estimated bytes of a movie's published HLS ladder (all rungs stored).
export function movieStoredBytes(movie) {
  const published = movie.render?.status === 'ready' && movie.streamUrl;
  if (!published) return 0;
  const dur = movie.durationSec || 0;
  return (dur * LADDER_KBPS * 1000) / 8; // kbps -> bytes
}

// Flat 1080p master MP4 kept once a movie has been analyzed for reframing.
export function movieMasterBytes(movie) {
  if (movie.master?.status !== 'ready') return 0;
  const dur = movie.master?.durationSec || movie.durationSec || 0;
  return (dur * MASTER_KBPS * 1000) / 8;
}

export function movieMonthlyStorage(movie) {
  return GB(movieStoredBytes(movie) + movieMasterBytes(movie)) * PRICES.s3StandardGbMonth;
}

// One-time compute to analyze a movie for reframing: compose the master MP4
// (~1.2× realtime) plus CPU person detection on sampled frames (~1× realtime).
export function reframeComputeOnce(movie) {
  if (!(movie.reframe?.status === 'ready' || movie.master?.status === 'ready')) return 0;
  const dur = movie.durationSec || 0;
  const fargatePerSec =
    (INFRA.fargateVcpu * PRICES.fargateVcpuHr + INFRA.fargateGb * PRICES.fargateGbHr) / 3600;
  return dur * 2.2 * fargatePerSec;
}

// Egress bytes for one full view at the assumed average bitrate.
export function movieBytesPerView(movie, a = DEFAULT_ASSUMPTIONS) {
  const dur = movie.durationSec || 0;
  return (dur * a.avgViewMbps * 1e6) / 8;
}

// ---- Ephemeral fan-out job cost model ------------------------------------
// Heavy jobs (RED .R3D decode, RAW re-decode) fan across short-lived Fargate
// runner tasks that bill per-second only while running. These assumptions mirror
// backend/shared/jobCost.js (keep the two in sync) and are deliberately
// conservative so the submission-time estimate is an upper-ish bound.
export const JOB_DEFAULTS = {
  vcpuPerTask: 8,
  gbPerTask: 16,
  maxChunks: 32,
  framesPerChunk: 240,
  decodeFpsPerTask: 7, // RED UHD "premium" decode throughput per 8-vCPU task
  downloadMBps: 120, // .RDC pull from S3 → EFS scratch
  perTaskOverheadSec: 25, // image pull + ffmpeg spin-up + teardown per task
  stitchPerMinSec: 6, // stitch (concat -c copy + audio) seconds per footage-minute
};

// Per-type effective throughput (mirror backend/shared/jobCost.js). An
// `r3d-master` re-decodes the RED negative AND re-encodes near-lossless, so it's
// far slower per frame than a `veryfast` `r3d-proxy` — using one flat rate made
// the master estimate read well below the metered actual.
const DECODE_FPS_BY_TYPE = { 'r3d-proxy': 7, 'r3d-master': 3 };

// Per-second on-demand price of one Fargate task at the given size.
export function fargatePerSecond(vcpu = JOB_DEFAULTS.vcpuPerTask, gb = JOB_DEFAULTS.gbPerTask) {
  return (vcpu * PRICES.fargateVcpuHr + gb * PRICES.fargateGbHr) / 3600;
}

// Inline asset renders (still grab / develop / grade) run on the always-on task
// (8 vCPU / 16 GB) rather than fanning out. Keep these in sync with the backend
// (backend/shared/renderJob.js) so the placeholder estimate matches the metered
// actual the worker writes back.
// still_red reflects the wall-clock of ONE RED frame when the always-on worker
// is baking several at once (STILLS_CONCURRENCY=4 shares 8 vCPU): full-sensor
// .R3D decode + perceive + grade + 16-bit + 3 derivatives. A lone bake is
// faster; the live progress/overrun model (eta.js) keeps the bar honest either way.
const RENDER_ESTIMATE_SEC = { still_red: 150, still_mp4: 14, develop: 25, grade: 18, clipGrade: 120 };
export function estimateRenderSec(kind, isRawVideo = false) {
  if (kind === 'still') return isRawVideo ? RENDER_ESTIMATE_SEC.still_red : RENDER_ESTIMATE_SEC.still_mp4;
  return RENDER_ESTIMATE_SEC[kind] || 20;
}
export function renderCostUsd(sec = 0) {
  return Math.max(0, sec) * fargatePerSecond(8, 16);
}

// Estimate the dollar cost + wallclock of a fan-out decode job. Pass whatever is
// known: exact `frames` (after a prep probe) is most accurate, else durationSec
// × srcFps is used. Returns billed task-seconds (sum across all tasks) and an
// estimated wallclock (chunks run in parallel).
export function estimateJobCost(
  { type = 'r3d-proxy', durationSec = 0, srcFps = 0, frames = 0, sizeBytes = 0, vcpuPerTask, gbPerTask, maxChunks, framesPerChunk } = {},
  opts = {}
) {
  const j = { ...JOB_DEFAULTS, ...opts };
  const vcpu = vcpuPerTask || j.vcpuPerTask;
  const gb = gbPerTask || j.gbPerTask;
  const fps = srcFps > 0 ? srcFps : 30;
  const totalFrames = frames > 0 ? frames : Math.round((durationSec || 0) * fps);
  const chunks = Math.min(maxChunks || j.maxChunks, Math.max(1, Math.ceil(totalFrames / (framesPerChunk || j.framesPerChunk))));
  const per = fargatePerSecond(vcpu, gb);
  const decodeFps = DECODE_FPS_BY_TYPE[type] || j.decodeFpsPerTask;

  const prepSec = Math.max(20, sizeBytes > 0 ? sizeBytes / (j.downloadMBps * 1e6) : (durationSec || 0) * 0.5) + 15;
  const decodeComputeSec = totalFrames / Math.max(1, decodeFps);
  const decodeTaskSec = decodeComputeSec + chunks * j.perTaskOverheadSec;
  const stitchSec = Math.max(20, ((durationSec || 0) / 60) * j.stitchPerMinSec) + 15;

  const taskSeconds = prepSec + decodeTaskSec + stitchSec;
  const usd = taskSeconds * per;
  const wallclockSec = prepSec + decodeComputeSec / Math.max(1, chunks) + j.perTaskOverheadSec + stitchSec;
  return {
    type,
    usd,
    taskSeconds,
    wallclockSec,
    chunks,
    vcpu,
    gb,
    frames: totalFrames,
    vcpuSeconds: taskSeconds * vcpu,
    breakdown: { prep: prepSec * per, decode: decodeTaskSec * per, stitch: stitchSec * per },
  };
}

// Actual cost of a finished/running job from the metered task-seconds the
// orchestrator records on jobs/{id}.cost. Single source of truth for pricing.
export function jobActualCost(job) {
  const c = job?.cost || {};
  if (c.vcpuSeconds != null || c.gbSeconds != null) {
    return (
      ((c.vcpuSeconds || 0) * PRICES.fargateVcpuHr + (c.gbSeconds || 0) * PRICES.fargateGbHr) / 3600
    );
  }
  // Fallback: assume the standard runner size against metered task-seconds.
  return (c.taskSeconds || 0) * fargatePerSecond();
}

// Estimate cost of publishing a movie: the RAW pre-pass fan-out (per RED clip)
// when re-decoding from RAW, plus the composite which runs on the always-on task
// (~$0 marginal but shown via renderComputeOnce).
export function estimatePublishCost(movie, redClips = []) {
  const o = movie.output || {};
  const wantRaw = o.height === 0 || o.redSource === 'raw';
  const composite = renderComputeOnce(movie);
  if (!wantRaw || !redClips.length) {
    return { usd: composite, fanout: 0, composite, jobs: [], wantRaw: false };
  }
  const jobs = redClips.map((c) =>
    estimateJobCost({
      type: 'r3d-master',
      durationSec: c.sourceDurationSec || c.durationSec || 0,
      srcFps: c.srcFps || c.sourceFps || 0,
      sizeBytes: c.sizeBytes || 0,
    })
  );
  const fanout = jobs.reduce((s, j) => s + j.usd, 0);
  return { usd: fanout + composite, fanout, composite, jobs, wantRaw: true };
}

// One-time backend compute to render/publish a movie. It runs on the always-on
// Fargate task so the *marginal* cash cost is ~zero, but this shows what that
// compute slice is worth (≈1.2× realtime across the 3-rung ladder on 2 vCPU).
export function renderComputeOnce(movie) {
  const dur = movie.durationSec || 0;
  const fargatePerSec =
    (INFRA.fargateVcpu * PRICES.fargateVcpuHr + INFRA.fargateGb * PRICES.fargateGbHr) / 3600;
  return dur * 1.2 * fargatePerSec;
}

// Aggregate everything into a monthly breakdown + per-trip / per-movie rows.
export function estimateCosts(trips = [], movies = [], assumptions = DEFAULT_ASSUMPTIONS) {
  const a = { ...DEFAULT_ASSUMPTIONS, ...assumptions };

  // ---- Storage -----------------------------------------------------------
  const rawStorage = trips.reduce((s, t) => s + tripMonthlyStorage(t), 0);
  const rawBytes = trips.reduce((s, t) => s + (t.totalBytes || 0), 0);
  const movieStorage = movies.reduce((s, m) => s + movieMonthlyStorage(m), 0);
  const movieBytes = movies.reduce((s, m) => s + movieStoredBytes(m), 0);

  // Derived RAW-develop assets (16-bit proxies + thumbnails + graded exports)
  // live on the movies bucket; estimated as a fraction of the raw footage bytes.
  const deriveBytes = rawBytes * (a.rawDeriveOverhead || 0);
  const deriveStorage = GB(deriveBytes) * PRICES.s3StandardGbMonth;

  // ---- Streaming (CloudFront egress), after the perpetual 1 TB free tier --
  const egressGb =
    movies.reduce((s, m) => s + GB(movieBytesPerView(m, a)) * a.viewsPerMonth, 0) +
    GB(rawBytes) * 0.02; // small hover-preview / thumbnail reads
  const billableEgressGb = Math.max(0, egressGb - PRICES.cloudfrontFreeGb);
  const grossStreaming = egressGb * PRICES.cloudfrontEgressGb; // before free tier
  const streaming = billableEgressGb * PRICES.cloudfrontEgressGb;

  // ---- Requests (CloudFront + S3), after free tiers ----------------------
  // Each view pulls ~one manifest + (dur / segment) media segments per rung it
  // touches; assume ~1.5 rungs effectively. Raw previews/thumbnails add a few.
  const reqPerView = movies.reduce((s, m) => {
    const segs = ((m.durationSec || 0) / SEGMENT_SEC) * 1.5 + 4;
    return s + segs;
  }, 0);
  const cfRequests = reqPerView * a.viewsPerMonth;
  const billableCfReq = Math.max(0, cfRequests - PRICES.cloudfrontFreeReq);
  const requests = (billableCfReq / 10_000) * PRICES.cloudfrontReqPer10k;

  // ---- Backend compute (always-on Fargate task) --------------------------
  // Publishing/waveform/thumbs all run on this same task, so there's no extra
  // per-publish charge.
  const fargatePerHr =
    INFRA.fargateVcpu * PRICES.fargateVcpuHr +
    INFRA.fargateGb * PRICES.fargateGbHr +
    Math.max(0, INFRA.fargateEphemeralGb - 20) * PRICES.fargateEphemeralGbHr;
  const compute = a.backendAlwaysOn ? fargatePerHr * HOURS_PER_MONTH : 0;

  // ---- Load balancer -----------------------------------------------------
  const loadBalancer = (PRICES.albHr + INFRA.albLcu * PRICES.albLcuHr) * HOURS_PER_MONTH;

  // ---- Other small fixed items -------------------------------------------
  const other =
    INFRA.ecrGb * PRICES.ecrGbMonth + INFRA.secrets * PRICES.secretsManagerSecretMonth;

  const breakdown = { rawStorage, movieStorage, deriveStorage, streaming, requests, compute, loadBalancer, other };
  const monthly = Object.values(breakdown).reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);

  // One-time render compute already "spent" on the published catalog.
  const renderComputeTotal = movies.reduce(
    (s, m) => s + (m.render?.status === 'ready' && m.streamUrl ? renderComputeOnce(m) : 0),
    0
  );

  // Lifetime storage projection for the current raw footage, per class.
  const storageProjection = STORAGE_CLASSES.map((c) => {
    const perMonth = GB(rawBytes) * c.price;
    return { ...c, perMonth, perYear: perMonth * 12, tenYear: perMonth * 120 };
  });

  const perTrip = trips
    .map((t) => {
      const monthlyT = tripMonthlyStorage(t);
      return {
        id: t.id,
        name: t.name || 'Untitled trip',
        bytes: t.totalBytes || 0,
        assetCount: t.assetCount || 0,
        storageClass: t.storageClass || 'STANDARD',
        monthly: monthlyT,
        yearly: monthlyT * 12,
      };
    })
    .sort((x, y) => y.monthly - x.monthly);

  const perMovie = movies
    .map((m) => {
      const published = m.render?.status === 'ready' && m.streamUrl;
      const storage = movieMonthlyStorage(m);
      const stream = published
        ? GB(movieBytesPerView(m, a)) * a.viewsPerMonth * PRICES.cloudfrontEgressGb
        : 0;
      return {
        id: m.id,
        title: m.title || 'Untitled',
        durationSec: m.durationSec || 0,
        published,
        storedBytes: movieStoredBytes(m),
        masterBytes: movieMasterBytes(m),
        analyzed: m.reframe?.status === 'ready',
        monthly: storage + stream,
        storage,
        stream,
        publishOnce: published ? renderComputeOnce(m) : 0,
        analyzeOnce: reframeComputeOnce(m),
      };
    })
    .sort((x, y) => y.monthly - x.monthly);

  return {
    monthly,
    daily: monthly / DAYS_PER_MONTH,
    yearly: monthly * 12,
    breakdown,
    egressGb,
    billableEgressGb,
    grossStreaming,
    cfRequests,
    rawBytes,
    movieBytes,
    deriveBytes,
    renderComputeTotal,
    storageProjection,
    perTrip,
    perMovie,
    assumptions: a,
  };
}

export const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};
